import React, { useContext, useEffect, useState, useRef } from 'react';
import { ArrowRight, Sparkles, Mic } from 'lucide-react';
import { useWord } from '../contexts/WordContext';
import { useAuth } from '../contexts/AuthContext';
import SynonymActivity from '../components/activities/SynonymActivity';
import FillBlankActivity from '../components/activities/FillBlankActivity';
import TrueFalseActivity from '../components/activities/TrueFalseActivity';
import Button from '../components/common/Button';
import LoginPrompt from './LoginPrompt';
import { searchWord } from '../services/dictionary';
import { translateText } from '../hooks/useTranslation';
import { getRandomWords } from '../components/activities/words.ts';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../store/useStore.tsx';
import FireworksReward from '../components/FireworksReward';
import SpeakActivity from '../components/activities/SpeakActivity.tsx';
import { updateWordDayStreak, getUserStats, fetchWordDayWeekActivity } from '../services/vocabularyService';
import { FeatureSummary } from '../components/gamification/FeatureSummary';

const Activities: React.FC = () => {
  const { currentUser } = useAuth();
  const { 
    currentWord, 
    activitiesCompleted,
    markActivityCompleted,
    setActivitiesCompleted,
    wordLoading,
    loadWordOfTheDay
  } = useWord();
  
  const [synonymActivity, setSynonymActivity] = useState(true)
  const [fillBlankActivity, setFillBlankActivity] = useState(false)
  const [trueFalseActivity, setTrueFalseActivity] = useState(false)
  const [speakyActivity, setSpeakyActivity] = useState(false)
  const [fillWord, setFillWord] = useState('')
  const [fillPhrase, setFillPhrase] = useState('')
  const [translated, setTranslated] = useState('')
  const [word, setWord] = useState('')
  const [answer, setAsnwer] = useState(false)
  const [question, setQuestion] = useState('')
  const [wordsData, setWordsData] = useState<string[][]>([]);
  const { getDayExercise, saveDayExercise, getAllDayExercises, wordOfDayPoints, setWordOfDayPoints } = useContext(UserContext)
  const [points, setPoints] = useState(0);
  const [noExamples, setNoExamples] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [previousStreak, setPreviousStreak] = useState(0);
  const [wordDayStreak, setWordDayStreak] = useState(0);
  const [wordDayWeekHistory, setWordDayWeekHistory] = useState<boolean[]>([]);
  const navigate = useNavigate();

  const allActivitiesCompleted = 
    activitiesCompleted.synonym && 
    activitiesCompleted.fillBlank && 
    activitiesCompleted.trueFalse && 
    activitiesCompleted.speaky;
    
  const hasAttemptedLoad = useRef(false);

  function removeRandomWord(phrase: string) {
    console.log('called')
    if(!phrase) {
      return ;
    }
    const words = phrase?.trim().split(' '); // Split by any whitespace
    if (words?.length === 0) {
      return { modifiedPhrase: "", removedWord: null };
    }
  
    const randomIndex = Math.floor(Math.random() * words.length);
    const removedWord = words[randomIndex]; // Get the word to remove
    
    const newWords = [...words];
    newWords[randomIndex] = '_____'; // Replace exactly that word in the array
  
    return {
      modifiedPhrase: newWords.join(' '),
      removedWord: removedWord
    };
  }

  const getFillActivity = async (activity: string, word: object) => {
    // const fillBlank = removeRandomWord(currentWord.details.examples[0]);
    if(currentWord?.details?.examples?.length && currentWord.details.examples[0].length > 0) {
      const fillBlank = removeRandomWord(currentWord.details.examples[0]);
      setFillWord(fillBlank.removedWord);
      setFillPhrase(fillBlank?.modifiedPhrase); 
    } else {
      setNoExamples(true)
    }
    return true; // Return boolean as expected
  }

  const getTrueOrFalseActivity = async (activity: string, word: object) => {
    let answer = Math.random() < 0.5;
    const correctDefinition = currentWord?.details?.meanings?.[0]?.definitions?.[0]?.definition;

    if (!correctDefinition) {
      answer = false; // Fallback: force a 'false' question if we lack the real definition
    }

    setAsnwer(answer);
    if (answer) {
      setQuestion(correctDefinition);
    } else {
      let wrongDefinition = '';
      const randomWords = getRandomWords(3);
      
      for (let i = 0; i < randomWords.length; i++) {
        try {
          const wrong = await searchWord(randomWords[i]);
          if (wrong && wrong.definition) {
            wrongDefinition = wrong.definition;
            break;
          }
        } catch (error) {
          console.warn(`Failed to fetch dictionary definition for random word: ${randomWords[i]}`);
        }
      }
      
      if (!wrongDefinition) {
        const fallbackDefinitions = [
          "a set of circumstances that makes it possible to do something.",
          "the solid surface of the earth.",
          "a person who is fully grown or developed.",
          "a building or group of buildings where goods are manufactured."
        ];
        wrongDefinition = fallbackDefinitions[Math.floor(Math.random() * fallbackDefinitions.length)];
      }
      
      setQuestion(wrongDefinition);
    }
    return true; // Return boolean as expected
  }

  const translate = async () => {
    const example = currentWord?.details?.examples?.[0];
    if (example) {
      const translated = await translateText(example.replace(/{\/?it}|\/it\//g, '').replace(/[\[\]=;]/g, ''), 'en', 'pt');
      setTranslated(translated);
    }
  }

  useEffect(() => {
    if (!currentWord && !wordLoading && !hasAttemptedLoad.current) {
      hasAttemptedLoad.current = true;
      loadWordOfTheDay();
      return;
    }

    console.log('Current word:', currentWord);
    if(currentWord) { // TODO: set to currentWord
      getFillActivity();
      translate();
      setWord(currentWord.word);
      getTrueOrFalseActivity()
      checkAllActivitiesCompleted();

      if(!currentWord?.details?.synonyms?.length && !noExamples) {
        setSynonymActivity(false);
        setFillBlankActivity(true);
      }
    }
  }, [currentWord, wordLoading, loadWordOfTheDay]);

  // Re-check completion status when activities are marked as completed
  // This ensures we detect when all activities are done even if they complete in quick succession
  useEffect(() => {
    if (currentWord && currentUser?.uid && !allActivitiesCompleted) {
      // Add a small delay to ensure Firebase writes have completed
      const timeoutId = setTimeout(() => {
        checkAllActivitiesCompleted();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [activitiesCompleted.synonym, activitiesCompleted.fillBlank, activitiesCompleted.trueFalse, activitiesCompleted.speaky, currentWord, currentUser?.uid, allActivitiesCompleted]);

  const checkAllActivitiesCompleted = async () => {
    const allDone = await getAllDayExercises(currentWord);
    console.log('All done:', allDone);
    if(allDone) {
      setActivitiesCompleted({
        synonym: true,
        fillBlank: true,
        trueFalse: true,
        speaky: true
      });
      // Update streak when all activities are completed
      if (currentUser?.uid) {
        // Store previous streak before updating
        const currentStats = await getUserStats(currentUser.uid);
        setPreviousStreak(currentStats.wordDayStreak || 0);
        
        await updateWordDayStreak(currentUser.uid, wordOfDayPoints.speak + wordOfDayPoints.fillBlank + wordOfDayPoints.synonym + wordOfDayPoints.trueFalse + wordOfDayPoints.podcast + 100);
        console.log('Activities.tsx - updateWordDayStreak completed');
        // Small delay to ensure database write completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh streak count and week history
        const updatedStats = await getUserStats(currentUser.uid);
        const newStreak = updatedStats.wordDayStreak || 0;
        setWordDayStreak(newStreak);
        
        // Fetch week history
        const weekHistory = await fetchWordDayWeekActivity(currentUser.uid);
        setWordDayWeekHistory(weekHistory);
        
        // Show summary immediately
        setShowSummary(true);
        
        // Dispatch custom event to notify other components
        console.log('Activities.tsx - Dispatching wordDayStreakUpdated event');
        window.dispatchEvent(new CustomEvent('wordDayStreakUpdated'));
      }
    } else {
      setActivitiesCompleted({
        synonym: false,
        fillBlank: false,
        trueFalse: false,
        speaky: false
      });
    }
  }

  useEffect(() => {
    if(noExamples) {
      setSynonymActivity(false);
      setFillBlankActivity(false);
      setTrueFalseActivity(true);
      saveDayExercise('fillBlank');
      saveDayExercise('speak');
    }
  }, [noExamples])

  // Load initial streak and week history when component mounts
  useEffect(() => {
    if (currentUser?.uid) {
      getUserStats(currentUser.uid).then(async stats => {
        const streak = stats.wordDayStreak || 0;
        setWordDayStreak(streak);
        setPreviousStreak(streak);
        
        // Load week history
        const weekHistory = await fetchWordDayWeekActivity(currentUser.uid);
        setWordDayWeekHistory(weekHistory);
      });
    }
  }, [currentUser]);

  // Early returns after all hooks
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoginPrompt />
      </div>)
  }

  if (wordLoading) {
    return (<div className="bg-white rounded-xl shadow-md p-12 flex justify-center items-center h-screen">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
          </div>)
  }

  if (!currentWord) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center p-8 bg-white rounded-xl shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No activities available</h2>
          <p className="text-gray-600">Please check back later for a new word of the day.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
          <>            
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Desafios</h2>
              <p className="text-gray-600 mb-6">Complete os desafios para reforçar o seu aprendizado e somar pontos no Ranking!</p>
              
              <div className="space-y-6">
              {showReward && (
                <FireworksReward onComplete={() => setShowReward(false)} points={points} />
              )}
                {synonymActivity && (
                  <div>
                    <SynonymActivity
                      currentWord={currentWord!}
                      correctAnswer={currentWord?.details?.synonyms[1] ? currentWord?.details.synonyms[1] : currentWord?.details.synonyms[0]}
                      onComplete={() => {
                        markActivityCompleted('synonym');
                        setSynonymActivity(false);
                        setFillBlankActivity(true);
                      }}
                      setSynonymActivity={setSynonymActivity}
                      setFillBlankActivity={setFillBlankActivity}
                      saveDayExercise={saveDayExercise}
                      getDayExercise={getDayExercise}
                      setShowReward={setShowReward}
                      allActivitiesCompleted={allActivitiesCompleted}
                      setPoints={setPoints}
                      onPointsEarned={(pts) => setWordOfDayPoints(prev => ({ ...prev, synonym: pts }))}
                    />
                  </div>
                )}
                
                {fillBlankActivity && (
                  <div>
                    <FillBlankActivity 
                      phrase={fillPhrase}
                      word={fillWord}                    
                      onComplete={() => {
                        markActivityCompleted('fillBlank');
                        setFillBlankActivity(false);
                        setTrueFalseActivity(true);
                      }} 
                      setFillBlankActivity={setFillBlankActivity}
                      setTrueFalseActivity={setTrueFalseActivity}
                      getFillActivity={getFillActivity}
                      translated={translated}
                      saveDayExercise={saveDayExercise}
                      getDayExercise={getDayExercise}
                      currentWord={currentWord}
                      setShowReward={setShowReward}
                      setPoints={setPoints}
                      onPointsEarned={(pts) => setWordOfDayPoints(prev => ({ ...prev, fillBlank: pts }))}
                    />
                  </div>
                )}
                
                {trueFalseActivity && (
                  <div>
                    <TrueFalseActivity 
                      currentWord={currentWord}
                      onComplete={() => {
                        markActivityCompleted('trueFalse');
                        setTrueFalseActivity(false);
                        setSpeakyActivity(true);
                      }}
                      setSpeakyActivity={setSpeakyActivity}
                      getTrueOrFalseActivity={getTrueOrFalseActivity}
                      setTrueFalseActivity={setTrueFalseActivity}
                      saveDayExercise={saveDayExercise}
                      getDayExercise={getDayExercise}
                      setShowReward={setShowReward}
                      wordLoading={wordLoading}
                      answer={answer}
                      question={question}
                      allActivitiesCompleted={allActivitiesCompleted}
                      setPoints={setPoints}
                      onPointsEarned={(pts) => setWordOfDayPoints(prev => ({ ...prev, trueFalse: pts }))}
                      noExamples={noExamples}
                    />
                  </div>
                )}

                  {speakyActivity && (
                  <div>
                    <SpeakActivity 
                      currentWord={currentWord!}
                      onComplete={() => {
                        markActivityCompleted('speaky');
                        setSpeakyActivity(false);
                      }}
                      setTrueFalseActivity={setTrueFalseActivity}
                      saveDayExercise={saveDayExercise}
                      getDayExercise={getDayExercise}
                      setShowReward={setShowReward}
                      setPoints={setPoints}
                      onPointsEarned={(pts) => setWordOfDayPoints(prev => ({ ...prev, speak: pts }))}
                      allActivitiesCompleted={allActivitiesCompleted}
                    />
                  </div>
                )}
              </div>
            </div>
            
            {allActivitiesCompleted && (
              <div className="mt-5 bg-purple-50 border border-purple-100 rounded-xl p-6 mb-8">
                <div className="flex flex-col items-center text-center md:flex-row md:justify-between md:items-center md:text-left">
                  {/* Icon + Text Section */}
                  <div className="flex flex-col items-center md:flex-row md:items-center">
                    <div className="bg-purple-100 rounded-full p-3 mb-2 md:mb-0 md:mr-4">
                      <Sparkles className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-purple-900">All activities completed!</h3>
                      <p className="text-purple-700 mt-2">Great job! Try a new word to continue learning.</p>
                    </div>
                  </div>
              
                  {/* Button Section */}
                  <div className="mt-4 md:mt-0 md:ml-auto">
                    <Button
                      onClick={() => navigate('/themes')}
                      className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-400 transition duration-200"
                      rightIcon={<ArrowRight className="h-4 w-4" />}
                    >
                      New Word
                    </Button>
                  </div>
                </div>
              </div>    
            )}
            {allActivitiesCompleted && (
              <div className="mt-1 bg-gray-50 border border-purple-400 rounded-xl p-6 mb-8">
                <div className="flex flex-col items-center text-center md:flex-row md:justify-between md:items-center md:text-left">
                  {/* Icon + Text Section */}
                  <div className="flex flex-col items-center md:flex-row md:items-center">
                    <div className="bg-purple-700 rounded-full p-3 mb-2 md:mb-0 md:mr-4">
                      <Mic className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-purple-900">Continue practicing the word</h3>
                      <p className="text-purple-700 mt-2">Go to the speak page to continue improving your speaking</p>
                    </div>
                  </div>
              
                  {/* Button Section */}
                  <div className="mt-4 md:mt-0 md:ml-auto">
                    <Button
                      onClick={() => navigate('/speaking')}
                      className="border border-purple-600 px-4 py-2 rounded-md hover:bg-gray-400 transition duration-200"
                      rightIcon={<ArrowRight className="h-4 w-4" />}
                    >
                      Practice
                    </Button>
                  </div>
                </div>
              </div>    
            )}

            {/* Inline Streak Summary - shown below activities when completed */}
            {showSummary && allActivitiesCompleted && currentUser && (
              <div className="mt-8 max-w-2xl mx-auto">
                <FeatureSummary
                  title="Perfect Lesson!"
                  message="You completed all activities for today's word!"
                  isPerfect={true}
                  currentStreak={wordDayStreak}
                  previousStreak={previousStreak}
                  weekHistory={wordDayWeekHistory}
                  onContinue={() => {
                    navigate('/dashboard');
                  }}
                  continueLabel="Continue Learning"
                  onSecondaryAction={() => {
                    navigate('/themes');
                  }}
                  secondaryLabel="Learn New Words"
                  colorScheme="pink"
                  inline={true}
                />
              </div>
            )}
          </>
        </div>
  );
};

export default Activities;